/**
 * ImageUploadNode (Canvas V2)
 *
 * Upload a local image file. Supports file picker and drag-and-drop.
 * 10MB limit, backend API upload, image preview with object-cover.
 * FloatingNodeLabel (editable) above the node, hidden output port.
 *
 * Uses a custom card wrapper matching ImageGeneratorNode's clean,
 * image-focused card style with boxShadow for state indication.
 * No BaseNode header bar — just the image content.
 *
 * 5 visual states:
 *   1. Empty — clickable label, upload icon + "Upload Image"
 *   2. Uploading — image preview + overlay spinner
 *   3. Error — error icon + message + retry hint
 *   4. Success — clean image only
 *   5. Hover — overlay buttons (expand, download, replace)
 *
 * Output: out_image (image)
 */

import { memo, useCallback, useMemo, useReducer, useState, useEffect, useRef, type CSSProperties } from 'react'
import { NODE_CARD_RADIUS_CLASS } from '../shared/constants'
import { createPortal } from 'react-dom'
import { NodeProps, Position } from 'reactflow'
import { useNodeActions, FloatingNodeLabel, NODE_TYPE_ICON, UploadMediaNodeToolbar, useRefreshMediaNodeInternals, generatedImagePath, getPickedIndex, FlatIdleNodeCard, resolveNodeErrorMessage, type HandleConfig } from '../shared'
import { AnnotationEditorOverlay, useImageAnnotation } from '../shared/annotate'
import {
  recordDecodedMediaDimensions,
  reservedMediaCacheKey,
  resolveReservedMediaBox,
  type ReservedMediaBox,
} from '../shared/reservedMediaBox'
import type { NodeDataRecord } from '../../nodeRegistry'
import { TypedHandle } from '../../TypedHandle'
import { useCanvasNodeChromeMode } from '../../CanvasNodeChromeModeContext'
import { useCanvasOptional } from '../../CanvasContext'
import { useBlockingOverlay } from '../../../../hooks/canvasShortcutGuard'
import { useCanvasNode } from '../../../../hooks/useCanvasNode'
import { useFileUpload } from '../shared/useFileUpload'
import { api } from '../../../../api/client'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { createNadDebug } from '../../../../utils/debugLog'

import { useTranslation } from 'react-i18next'
import { dataStatus,dataStr,dataStrOpt } from '../../../../hooks/useYCanvas'
import { type ReactFlowCanvasNodeDataByType } from '../../../../generated/nodeDataTypes'
import { useRegisterNodeContextAction } from '../../contextMenu/nodeContextActions'

type ImageUploadNodeData = ReactFlowCanvasNodeDataByType['image']

/** Shared style for both handles: present in the DOM so edges can anchor to
 *  them, but never visible or interactive. An Image Upload node has no
 *  user-facing connector — `in_image` exists only as the anchor for the
 *  provenance edge an annotation bake draws from its source image node. */
const INVISIBLE_HANDLE_STYLE: CSSProperties = {
  top: '50%',
  opacity: 0,
  border: 'none',
  background: 'transparent',
  pointerEvents: 'none',
}

/**
 * The box an uploaded image reserves on the canvas comes from
 * `resolveReservedMediaBox` (persisted upload dimensions → session decode cache
 * → persisted aspect bucket → 16:9), resolved once in the outer
 * `ImageUploadNode` and handed to both cards.
 *
 * Two invariants depend on that:
 *  - The flat and full cards reserve an identical box, so a hover swap cannot
 *    shrink the node's own hit box out from under the pointer (T289141129).
 *  - The box is known before the bitmap decodes and survives unmount. With
 *    `onlyRenderVisibleElements` a pan unmounts and remounts off-screen nodes;
 *    a box held in component state fell back to 16:9 on every remount, which
 *    re-derived the parent group's grid and moved every sibling (the group
 *    scroll glitch). `computeCellSize` resolves the same box for unmounted
 *    children, so the group reserves exactly what the card renders.
 */

/**
 * One line per flat/full swap, carrying the box each card reserves.
 *
 * This is the trace that makes the oscillation legible: while the bug was
 * live, resting the pointer on the boundary produced an unbroken alternating
 * run of `card=flat reserved_width=315` / `card=full reserved_width=236` for
 * as long as the pointer sat there. Fixed, a hover is two lines with the same
 * `reserved_width`. A repeating width flip in this trace is the regression.
 */
const hoverBoxLog = createNadDebug('dw:image-node-hover-box:reserved-box')

const FLAT_IMAGE_HANDLES = [
  {
    id: 'in_image',
    type: 'target',
    position: Position.Left,
    style: INVISIBLE_HANDLE_STYLE,
  },
  {
    id: 'out_image',
    type: 'source',
    position: Position.Right,
    style: INVISIBLE_HANDLE_STYLE,
  },
] satisfies HandleConfig[]

interface FlatImageUploadNodeProps extends NodeProps<ImageUploadNodeData> {
  reservedBox: ReservedMediaBox
}

const FlatImageUploadNode = memo(({ id, data, reservedBox }: FlatImageUploadNodeProps) => {
  const { t } = useTranslation('canvas')
  const status = dataStatus(data)
  const error = dataStrOpt(data, 'error')
  const node = useCanvasNode(id, 'image', data)
  const projectId = useProjectId()

  const out_image = node?.get('out_image')
  const in_file_name = node?.get('in_file_name')
  const outImageHistory = out_image?.history ?? []
  const outImagePath = generatedImagePath(outImageHistory[getPickedIndex(out_image)])
  const displayUrl = outImagePath ? api.getAssetUrl(projectId, outImagePath) : undefined
  const label =
    dataStrOpt(data, 'label') ||
    in_file_name?.replace(/\.[^.]+$/, '') ||
    undefined

  return (
    <FlatIdleNodeCard
      nodeId={id}
      testId="image-upload-flat-node"
      label={label}
      fallbackLabel={t('imageUploadNode.image')}
      typeIcon={NODE_TYPE_ICON.image}
      // Must match FullImageUploadNode exactly — a box that differs between the
      // two cards makes hover change the node's own hit area (T289141129).
      width={reservedBox.width}
      status={status}
      handles={FLAT_IMAGE_HANDLES}
      mediaKind={displayUrl ? 'image' : 'none'}
      mediaUrl={displayUrl}
      aspectRatio={reservedBox.aspectRatio}
      error={status === 'error' ? resolveNodeErrorMessage(error, t) : undefined}
      placeholderLabel={t('imageUploadNode.image')}
      compactHandles
    />
  )
})

FlatImageUploadNode.displayName = 'FlatImageUploadNode'

interface FullImageUploadNodeProps extends NodeProps<ImageUploadNodeData> {
  reservedBox: ReservedMediaBox
  /** Called after a decode taught the session cache something new. */
  onDecodedDimensions: () => void
}

const FullImageUploadNode = memo(({
  id,
  data,
  selected,
  dragging,
  reservedBox,
  onDecodedDimensions,
}: FullImageUploadNodeProps) => {
  const nodePresentationWidth = useNodePresentationWidth()
  const { previewStyle, mediaFit } = useNodePresentationPreview()
  const { t } = useTranslation('canvas')
  const status = dataStatus(data)
  const error = dataStrOpt(data, 'error')

  const canvas = useCanvasOptional()
  const node = useCanvasNode(id, 'image', data)
  const projectId = useProjectId()

  const out_image = node?.get('out_image')
  const in_file_name = node?.get('in_file_name')

  // Track whether the label was manually set by the user
  const labelManuallySet = useRef(false)

  // Local state for immediate label display (Issue 2: frontend-origin YDoc
  // writes skip React state rebuild, so we need local state for instant feedback)
  const [derivedLabel, setDerivedLabel] = useState<string>('')

  const updateNodeData = useCallback(
    (updates: NodeDataRecord<'image'>) => {
      // Auto-set label from uploaded filename (if not manually renamed)
      const merged = { ...updates }
      if (updates.in_file_name && typeof updates.in_file_name === 'string' && !labelManuallySet.current) {
        const nameWithoutExt = updates.in_file_name.replace(/\.[^.]+$/, '')
        merged.label = nameWithoutExt
        setDerivedLabel(nameWithoutExt)
      }
      // Write schema-declared fields via typed node handle
      if (merged.in_file_name !== undefined && typeof merged.in_file_name === 'string') {
        node?.writeInFileName(merged.in_file_name)
      }
      // Write remaining non-schema fields via canvas
      canvas?.updateNodeData({ id, type: 'image' }, merged)
    },
    [id, canvas, node]
  )

  const {
    previewUrl,
    isUploading,
    handleFileChange,
    dragHandlers,
    isDragOver,
    retry,
    multiFileWarning,
    uploadFile,
  } = useFileUpload({
    nodeId: id,
    acceptMime: 'image',
    updateNodeData,
    persistedValue: out_image,
    initialPreviewUrl: dataStrOpt(data, '_initialPreviewUrl'),
  })

  // Auto-upload initialFile when canvas context is ready
  const initialFile = data.initialFile instanceof File ? data.initialFile : undefined
  const initialFileProcessed = useRef(false)
  useEffect(() => {
    if (initialFile && canvas && !initialFileProcessed.current) {
      initialFileProcessed.current = true
      uploadFile(initialFile)
    }
  }, [initialFile, canvas, uploadFile])

  // Clear initialFile from node data after processing to avoid re-uploading on re-render
  const initialFileCleared = useRef(false)
  useEffect(() => {
    if (initialFile && !initialFileCleared.current) {
      initialFileCleared.current = true
      // Use a timeout to let the upload start before clearing
      setTimeout(() => {
        canvas?.updateNodeData({ id, type: 'image' }, { initialFile: undefined })
      }, 100)
    }
  }, [initialFile, id, canvas])

  const { handleDownload } = useNodeActions({ nodeId: id })

  const outImageHistory = out_image?.history ?? []
  const outImagePath = generatedImagePath(outImageHistory[getPickedIndex(out_image)])

  // Use either the object URL preview or derive from the persisted out_image path
  const persistedUrl = outImagePath ? api.getAssetUrl(projectId, outImagePath) : undefined
  const displayUrl = previewUrl || persistedUrl

  const onDownload = useCallback(() => {
    if (outImagePath) {
      handleDownload(outImagePath, in_file_name || 'uploaded-image.jpg')
    }
  }, [outImagePath, in_file_name, handleDownload])
  useRegisterNodeContextAction(id, 'download', onDownload, Boolean(outImagePath))

  // The persisted box is resolved by the outer ImageUploadNode and shared with
  // the flat card. Only a local preview (upload still in flight) has no
  // persisted metadata yet; it resolves from the decode cache keyed by its
  // object URL, falling back to 16:9 until the preview decodes.
  const box = previewUrl ? resolveReservedMediaBox(undefined, previewUrl, projectId) : reservedBox
  const aspectRatio = box.source === 'schema-default' ? null : box.aspectRatio
  // Node width snapped to closest ASPECT_WIDTH_MAP bucket; default 16:9 = 315px
  const nodeWidth = box.width
  useRefreshMediaNodeInternals({
    nodeId: id,
    projectId,
    mediaKind: 'image',
    nodeWidth,
    aspectRatio,
    hasMedia: !!displayUrl,
  })

  // Lightbox state
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Suspend canvas keyboard shortcuts while this overlay is open (see canvasShortcutGuard).
  useBlockingOverlay(isModalOpen)

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget
      // Same key `resolveReservedMediaBox` reads: the preview URL while an
      // upload is in flight, otherwise the picked asset path.
      const cacheKey = previewUrl || reservedMediaCacheKey(out_image)
      if (recordDecodedMediaDimensions(projectId, cacheKey, img.naturalWidth, img.naturalHeight)) {
        onDecodedDimensions()
      }
    },
    [projectId, previewUrl, out_image, onDecodedDimensions],
  )

  // No explicit reset when the image is cleared or replaced: the decode cache
  // is keyed by asset path, so a new image resolves its own box.

  const handleRename = useCallback(
    (newName: string) => {
      labelManuallySet.current = true
      setDerivedLabel(newName)
      canvas?.updateNodeData({ id, type: 'image' }, { label: newName })
    },
    [id, canvas]
  )

  const inputId = `file-upload-${id}`
  const isError = status === 'error'
  // RFC-1115: the plain `image` node is annotatable too, which is what makes
  // "re-annotate by annotating the annotated node" work (RFC §2) — the node
  // this feature creates is itself an `image` node.
  const annotate = useImageAnnotation({
    nodeId: id,
    feNodeType: 'image',
    imagePath: outImagePath,
    imageUrl: persistedUrl,
    projectId,
  })

  const hasError = isError && !!error

  return (
    <>
      <div className="relative image-upload-node-root" style={{ width: nodePresentationWidth(nodeWidth) }}>

        {/* RFC-1115 annotation editor. Mounted on the node ROOT, deliberately
            OUTSIDE `image-upload-node-chrome`: globals.css gives that wrapper
            `display: none !important` while the viewport is interacting or a
            node is dragging, so an editor nested inside it would blink out
            mid-pan. The root also has no clipping ancestor for the palette. */}
        {annotate.isOpen && persistedUrl && (
          <AnnotationEditorOverlay
            imageUrl={persistedUrl}
            altText={in_file_name || t('imageUploadNode.altUploadedImage')}
            traceId={annotate.traceId}
            nodeId={id}
            isSaving={annotate.isSaving}
            onCancel={annotate.close}
            onSave={annotate.save}
          />
        )}
        <div className="image-upload-node-chrome">
          <UploadMediaNodeToolbar
            nodeId={id}
            selected={selected}
            dragging={dragging}
            extraActions={[annotate.toolbarAction]}
            originalName={derivedLabel || dataStr(data, 'label') || t('imageUploadNode.untitled')}
          />


          {/* Floating editable label above the card */}
          <FloatingNodeLabel
            name={derivedLabel || dataStr(data, 'label') || ''}
            fallback={t('imageUploadNode.untitled')}
            typeIcon={NODE_TYPE_ICON.image}
            onRename={handleRename}
          />
        </div>

        {/* Card wrapper — matches ImageGeneratorNode's boxShadow pattern */}
        <div
          className={`canvas-node-card-contained image-upload-node-card ${NODE_CARD_RADIUS_CLASS} overflow-hidden`}
          style={{
            width: nodePresentationWidth(nodeWidth),
            boxShadow: selected
              ? '0 0 0 2px rgba(255,255,255,0.7)'
              : hasError
                ? '0 0 0 1px rgba(239,68,68,0.5)'
                : '0 2px 8px rgba(0,0,0,0.3)',
          }}
          {...dragHandlers}
        >
          {/* Hidden file input */}
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="image-upload-node-chrome hidden"
            id={inputId}
            disabled={isUploading}
          />

          {/* Main content area — 5 visual states */}
          {isUploading && displayUrl ? (
            /* ── State 2: Uploading — image preview + overlay spinner ── */
            <div
              className="relative image-upload-node-preview-shell"
              style={{ aspectRatio: aspectRatio || '16/9', overflow: 'hidden', ...previewStyle }}
            >
              <img
                data-testid="image-upload-node-preview-image"
                src={displayUrl}
                style={{ objectFit: mediaFit }}
                alt={in_file_name || t('imageUploadNode.altUploadingImage')}
                className="image-upload-node-preview-image w-full h-full object-cover block"
                onLoad={handleImageLoad}
              />
              <div className="image-upload-node-chrome absolute inset-0 bg-black/50 flex flex-col items-center justify-center">
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span className="text-xxs text-white/80 mt-2">{t('imageUploadNode.uploadingImage')}</span>
              </div>
            </div>
          ) : hasError ? (
            /* ── State 3: Error — icon + message + retry ── */
            <div
              className="image-upload-node-chrome relative w-full bg-canvas flex flex-col items-center justify-center px-6 cursor-pointer"
              style={{ aspectRatio: '16/9', ...previewStyle }}
              onClick={retry}
            >
              <svg
                className="w-8 h-8 text-foreground/25"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
              <span className="text-xs font-medium text-foreground/50 mt-1.5">{t('imageUploadNode.clickToRetry')}</span>
              {/* Status overlay — the shared, neutral treatment (post 1634117708345526). */}
              <NodeStatusMessage
                message={resolveNodeErrorMessage(error, t) || t('imageUploadNode.failedToUpload')}
                testId="upload-card-error"
              />
            </div>
          ) : displayUrl ? (
            /* ── State 4: Success — clean image ── */
            <div
              className="relative group image-upload-node-preview-shell"
              style={{ aspectRatio: aspectRatio || '16/9', overflow: 'hidden', ...previewStyle }}
            >

              <img
                data-testid="image-upload-node-preview-image"
                src={displayUrl}
                style={{ objectFit: mediaFit }}
                alt={in_file_name || t('imageUploadNode.altUploadedImage')}
                className={`image-upload-node-preview-image w-full h-full object-cover block transition-transform duration-300 ease-out ${
                  !selected ? 'group-hover:scale-[1.02]' : ''
                }`}
                onLoad={handleImageLoad}
              />

              {/* ── State 5: Hover overlay buttons — expand, download, replace ── */}
              <div className="image-upload-node-chrome absolute top-2 right-2 flex gap-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                {/* Expand */}
                <button
                  onClick={(e) => { e.stopPropagation(); setIsModalOpen(true) }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                  title={t('imageUploadNode.expand')}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M20 8V4m0 0h-4M4 16v4m0 0h4M20 16v4m0 0h-4" />
                  </svg>
                </button>
                {/* Download */}
                <button
                  onClick={(e) => { e.stopPropagation(); onDownload() }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                  title={t('imageUploadNode.download')}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </button>
                {/* Replace — label triggers file picker */}
                <label
                  htmlFor={inputId}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors cursor-pointer nodrag"
                  title={t('imageUploadNode.replace')}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </label>
              </div>
            </div>
          ) : (
            /* ── State 1: Empty — clickable upload area ── */
            <label
              htmlFor={inputId}
              className={`image-upload-node-chrome w-full bg-canvas flex flex-col items-center justify-center cursor-pointer transition-colors ${
                isDragOver
                  ? 'bg-primary/5 ring-2 ring-primary/40 ring-inset'
                  : ''
              }`}
              style={{ aspectRatio: '16/9', ...previewStyle }}
            >
              {isDragOver ? (
                <span className="text-xs font-medium text-primary">{t('imageUploadNode.dropToUpload')}</span>
              ) : (
                <>
                  <svg
                    className="w-8 h-8 text-foreground/15"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                    />
                  </svg>
                  <span className="text-xxs text-foreground/30 mt-1">{t('imageUploadNode.uploadImage')}</span>
                </>
              )}
            </label>
          )}

          {/* Multi-file drop warning */}
          {multiFileWarning && (
            <div className="image-upload-node-chrome px-2 py-1.5 bg-amber-500/10 border-t border-amber-500/30">
              <span className="text-xxs text-amber-400">{multiFileWarning}</span>
            </div>
          )}
        </div>

        {/* Input handle: in_image — visually hidden, kept in DOM so an
            annotation bake's provenance edge from the source image node has
            something to anchor to. Never user-connectable. */}
        <TypedHandle
          nodeId={id}
          type="target"
          position={Position.Left}
          id="in_image"
          portType="image"
          size={2}
          style={INVISIBLE_HANDLE_STYLE}
        />
        {/* Output handle: out_image — visually hidden, kept in DOM for edges */}
        <TypedHandle
          nodeId={id}
          type="source"
          position={Position.Right}
          id="out_image"
          portType="image"
          size={2}
          style={INVISIBLE_HANDLE_STYLE}
        />
      </div>

      {/* Fullscreen Lightbox */}
      {isModalOpen && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
            >
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={displayUrl}
              alt={t('imageUploadNode.altFullSize')}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>,
        document.body
      )}
    </>
  )
})

FullImageUploadNode.displayName = 'FullImageUploadNode'

const ImageUploadNode = memo((props: NodeProps<ImageUploadNodeData>) => {
  const { lightweightImageNodeShell, lightweightNodeChrome } = useCanvasNodeChromeMode()
  const [isHoveringLightweightShell, setIsHoveringLightweightShell] = useState(false)
  // Bumped when a decode adds to the session cache, so both cards re-resolve.
  // The box itself lives outside React state (see reservedMediaBox.ts) so it
  // survives the flat/full swap and viewport-culling unmounts alike.
  const [decodeRevision, bumpDecodeRevision] = useReducer((n: number) => n + 1, 0)
  // Same live YDoc read the cards use, so the box and the displayed image
  // always come from one snapshot of `out_image`.
  const outImage = useCanvasNode(props.id, 'image', props.data)?.get('out_image')
  const projectId = useProjectId()
  const reservedBox = useMemo(
    () => resolveReservedMediaBox(outImage, undefined, projectId),
    // decodeRevision is the cache-invalidation signal for the module-level cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [outImage, projectId, decodeRevision],
  )

  const shouldUseLightweightShell = lightweightImageNodeShell || lightweightNodeChrome
  const shouldRenderFullChrome =
    !shouldUseLightweightShell ||
    !!props.selected ||
    !!props.dragging ||
    isHoveringLightweightShell

  // Both cards render `reservedBox`, so this width is the one the node
  // actually reserves regardless of which card is mounted. `source` says where
  // it came from; `schema-default` for an image with persisted metadata is a
  // regression (the box would flip once the bitmap decodes).
  useEffect(() => {
    hoverBoxLog({
      event: 'image-node-reserved-box',
      node_id: props.id,
      card: shouldRenderFullChrome ? 'full' : 'flat',
      lightweight_shell: shouldUseLightweightShell,
      reserved_width: reservedBox.width,
      reserved_aspect: reservedBox.aspectRatio,
      source: reservedBox.source,
    })
  }, [props.id, shouldRenderFullChrome, shouldUseLightweightShell, reservedBox])

  if (!shouldUseLightweightShell) {
    return (
      <FullImageUploadNode
        {...props}
        reservedBox={reservedBox}
        onDecodedDimensions={bumpDecodeRevision}
      />
    )
  }

  return (
    <div
      data-testid="image-upload-lightweight-shell-host"
      className="w-fit"
      onPointerEnter={() => setIsHoveringLightweightShell(true)}
      onPointerLeave={() => setIsHoveringLightweightShell(false)}
    >
      {shouldRenderFullChrome ? (
        <FullImageUploadNode
          {...props}
          reservedBox={reservedBox}
          onDecodedDimensions={bumpDecodeRevision}
        />
      ) : (
        <FlatImageUploadNode {...props} reservedBox={reservedBox} />
      )}
    </div>
  )
})

ImageUploadNode.displayName = 'ImageUploadNode'
export default ImageUploadNode
import { useNodePresentationWidth, useNodePresentationPreview } from '../../NodePresentationContext'
import { NodeStatusMessage } from '../shared/NodeStatusMessage'
